import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';

import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    setSceneWatermarkOverrideOperation,
    setSceneWatermarkPositionOperation,
} from 'js/src/luminary/luminary-operations';

export interface SceneWatermarkEdit {
    visibility?: boolean;
    watermark_source_index?: number;
    position?: string;
}

/**
 * Apply any combination of a scene's watermark source, position and visibility in one call.
 *
 * Every parameter is validated before anything changes, so an invalid one leaves the scene as it
 * was. Valid ones apply in a fixed order: source, then position, then visibility. Hiding wins over
 * a source given in the same call, and the result says so rather than silently dropping it.
 */
export function applySceneWatermarkEdit(
    luminary: Luminary,
    dispatch: AppDispatch,
    scene: Scene,
    sceneLabel: string,
    { visibility, watermark_source_index, position }: SceneWatermarkEdit,
    parentActionGroup: string,
): string {
    if (watermark_source_index === undefined && !position && visibility === undefined) {
        return 'Error: Please provide visibility (to show or hide the watermark), watermark_source_index (to change its source), position (to move it), or any combination of them';
    }

    // The watermark is part of the scene design; a design without one cannot be given one.
    if (!scene.watermark) {
        return `Error: No watermark available for editing on scene ${sceneLabel}.`;
    }

    const sourceCount = luminary.template.watermarkSources.length;
    if (
        watermark_source_index !== undefined &&
        (watermark_source_index < 0 || watermark_source_index >= sourceCount)
    ) {
        return sourceCount === 0
            ? `Error: watermark_source_index ${watermark_source_index} is out of range: this template has no watermark sources.`
            : `Error: watermark_source_index ${watermark_source_index} is out of range: use 0-${sourceCount - 1}.`;
    }
    if (position && !scene.watermark.availablePositions.includes(position)) {
        return `Error: Invalid position "${position}". Available positions are: ${scene.watermark.availablePositions.join(', ')}`;
    }

    const { setSceneWatermarkOverride, setSceneWatermarkPosition } = makeOperationsCallable(
        { setSceneWatermarkOverrideOperation, setSceneWatermarkPositionOperation },
        luminary,
        dispatch,
        () => null as unknown as RootState,
    );

    const hiding = visibility === false;
    const changes: string[] = [];
    const notes: string[] = [];

    if (watermark_source_index !== undefined) {
        if (hiding) {
            notes.push(
                `watermark source index ${watermark_source_index} was not applied because the watermark is hidden`,
            );
        } else {
            setSceneWatermarkOverride(scene.id, watermark_source_index, false, parentActionGroup);
            changes.push(`source index ${watermark_source_index}`);
        }
    }
    if (position) {
        setSceneWatermarkPosition(scene.id, position, false, parentActionGroup);
        changes.push(`position ${position}`);
    }
    if (hiding) {
        setSceneWatermarkOverride(scene.id, null, false, parentActionGroup);
        changes.push('hidden');
    } else if (visibility === true && watermark_source_index === undefined) {
        setSceneWatermarkOverride(scene.id, 0, false, parentActionGroup);
        changes.push('shown with source index 0');
    }

    const summary = `Success: Updated watermark in scene ${sceneLabel}: ${changes.join(', ')}.`;
    return notes.length > 0 ? `${summary} Note: ${notes.join('; ')}.` : summary;
}
